/**
 * Saturn `StakedUSDat` — the read surface the `saturn-vault` reader calls.
 *
 * The 4626 *view* side of this vault is honest (`totalAssets`, `totalSupply`,
 * `convertToAssets`, `previewDeposit`, `maxDeposit` all answer correctly), so
 * only the three dials the generic reader cannot see are declared here.
 *
 * The 4626 *write* side is where it diverges, and that belongs to the calldata
 * layer: `withdraw()` and `redeem()` are `pure` stubs that revert
 * `OperationNotAllowed()`, and `maxWithdraw()` is `pure` returning 0. NB
 * `maxRedeem` is NOT one of those — it is a normal `paused() ? 0 :
 * balanceOf(owner)`, so a zero read against an address with no shares says
 * nothing about whether the vault can be exited.
 */

/** `MIN_WITHDRAWAL()` — the smallest `requestRedeem` the queue accepts, in
 *  raw USDat (1e7 = 10 USDat). Read live rather than pinned: it decides
 *  whether the row's only exit route is reachable at all. */
export const SaturnMinWithdrawalAbi = [
  {
    name: 'MIN_WITHDRAWAL',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

/**
 * `depositFeeBps()` — an entry haircut on a `DEFAULT_ADMIN_ROLE` dial,
 * `0` at integration against a `MAX_DEPOSIT_FEE_BPS` of 500.
 *
 * Read live for the reason `erc4626-cooldown` reads `cooldownDuration` live:
 * the docs say 10 bps, the chain says 0, and a pinned number is wrong in
 * whichever direction governance moves next. `previewDeposit` already nets it
 * (`_deposit` charges it as a separate `transferFrom` to `feeRecipient` and
 * mints on the remainder), so the reader takes the ENTRY price from there
 * rather than from `convertToShares`.
 */
export const SaturnDepositFeeAbi = [
  {
    name: 'depositFeeBps',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

/** `previewDeposit(assets)` — shares minted for `assets`, NET of
 *  `depositFeeBps`. `convertToShares` is the gross figure and would overstate
 *  the entry the moment the dial is turned on. */
export const SaturnPreviewDepositAbi = [
  {
    name: 'previewDeposit',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'assets', type: 'uint256' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const
